import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import apiClient from '../utils/axiosConfig';
import { API_BASE_URL, LOGO_BASE_URL } from '../config';
import AppShell from '../components/layout/AppShell';
import useDocumentTitle from '../hooks/useDocumentTitle';
import '../styles/LoginPage.css';

function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [fullName, setFullName] = useState('');
  const [country, setCountry] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [betaRestricted, setBetaRestricted] = useState(false);
  const [phoneBlocked, setPhoneBlocked] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState(3);
  const [businessTitle, setBusinessTitle] = useState(null);

  useDocumentTitle(businessTitle ? `تسجيل الدخول · ${businessTitle}` : 'تسجيل الدخول');

  const LOGIN_RETURN_KEY = 'loginReturnTo';

  const renderStepDots = () => (
    <div className="login-steps" aria-hidden="true">
      {[1, 2, 3].map((s) => (
        <span
          key={s}
          className={`login-step-dot ${step === s ? 'active' : ''} ${step > s ? 'done' : ''}`}
        />
      ))}
    </div>
  );

  const getBusinessId = () => searchParams.get('businessid');

  const loginNavBack = () => {
    const businessId = getBusinessId();
    if (!businessId) return null;
    return {
      onClick: () => navigate(`/?businessid=${encodeURIComponent(businessId)}`),
      label: businessTitle || 'المحل',
      ariaLabel: 'العودة لصفحة المحل',
    };
  };

  const loginCard = (content) => (
    <AppShell navBack={loginNavBack()}>
      <div className="login-page">
        <div className="login-container">
          {content}
          <Link to="/" className="login-back-home">
            ← العودة إلى قائمة الصالونات
          </Link>
        </div>
      </div>
    </AppShell>
  );

  useEffect(() => {
    const businessId = searchParams.get('businessid');
    if (!businessId) {
      setBusinessTitle(null);
      return;
    }
    apiClient.get(`/accounts/${businessId}`)
      .then((res) => {
        if (res.data?.success && res.data.account?.title) {
          setBusinessTitle(res.data.account.title);
        } else {
          setBusinessTitle(null);
        }
      })
      .catch(() => {
        setBusinessTitle(null);
      });
  }, [searchParams]);

  const getPostLoginTarget = () => {
    try {
      const saved = sessionStorage.getItem(LOGIN_RETURN_KEY);
      if (!saved) return null;
      const { pathname, search } = JSON.parse(saved);
      const hasBusinessId = search && new URLSearchParams(search).get('businessid');
      if (hasBusinessId) return pathname + search;
      return '/';
    } catch (_) {
      return null;
    }
  };

  const clearLoginReturn = () => {
    sessionStorage.removeItem(LOGIN_RETURN_KEY);
  };

  const navigateAfterLogin = () => {
    const target = getPostLoginTarget();
    clearLoginReturn();
    if (target) {
      navigate(target);
    } else {
      const businessId = getBusinessId();
      navigate(businessId ? `/calendar?businessid=${businessId}` : '/');
    }
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/send-code`, { phone });
      if (response.data.success) {
        setRemainingAttempts(3);
        setVerificationCode('');
        setStep(2);
      } else {
        setError(response.data.message || 'حدث خطأ في إرسال الكود');
      }
    } catch (err) {
      if (err.response?.data?.beta_restricted) setBetaRestricted(true);
      else if (err.response?.data?.phone_blocked) setPhoneBlocked(true);
      else setError(err.response?.data?.message || 'حدث خطأ في الاتصال بالخادم');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/verify-code`, {
        phone,
        code: verificationCode,
      });
        if (response.data.success) {
          if (response.data.user_exists && response.data.user) {
            setRemainingAttempts(3);
            login(
              {
                id: response.data.user.id,
                phone: response.data.user.phone,
                full_name: response.data.user.full_name,
                country: response.data.user.country,
              },
              response.data.access_token
            );
            navigateAfterLogin();
          } else if (response.data.beta_restricted) {
            setBetaRestricted(true);
          } else {
            setRemainingAttempts(3);
            setPhone(response.data.phone || phone);
            setStep(3);
          }
      } else {
        setError(response.data.message || 'الكود غير صحيح');
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.phone_blocked) setPhoneBlocked(true);
      else if (data?.max_attempts_reached) {
        setError('تم استنفاد جميع المحاولات. يرجى طلب كود جديد');
        setRemainingAttempts(0);
        setTimeout(() => {
          setStep(1);
          setVerificationCode('');
          setError('');
          setRemainingAttempts(3);
        }, 2500);
      } else {
        if (data?.remaining_attempts !== undefined) setRemainingAttempts(data.remaining_attempts);
        setError(data?.message || 'الكود غير صحيح');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteRegistration = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/complete-registration`, {
        phone,
        full_name: fullName,
        country,
      });
      if (response.data.success) {
        login(
          {
            id: response.data.user.id,
            phone: response.data.user.phone,
            full_name: response.data.user.full_name,
            country: response.data.user.country,
          },
          response.data.access_token
        );
        navigateAfterLogin();
      } else {
        setError(response.data.message || 'حدث خطأ في التسجيل');
      }
    } catch (err) {
      if (err.response?.data?.beta_restricted) setBetaRestricted(true);
      else setError('حدث خطأ في التسجيل');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const businessLogo = getBusinessId() ? (
    <img
      src={`${LOGO_BASE_URL}/${getBusinessId()}`}
      alt={businessTitle || 'Logo'}
      className="login-logo"
      onError={(e) => { e.target.style.display = 'none'; }}
    />
  ) : null;

  if (phoneBlocked) {
    return loginCard(
      <>
        <div className="login-header">
          {businessLogo}
          <h1>تم حظر الرقم</h1>
        </div>
        <div className="beta-message">
          <div className="beta-icon">🚫</div>
          <p className="beta-title">تم حظر رقم الهاتف هذا</p>
          <p className="beta-description">تم حظر هذا الرقم بسبب طلبات تحقق مشبوهة. إذا كنت تعتقد أن هذا خطأ، يرجى التواصل مع الدعم.</p>
        </div>
      </>
    );
  }

  if (betaRestricted) {
    return loginCard(
      <>
        <div className="login-header">
          {businessLogo}
          <h1>نسخة تجريبية</h1>
        </div>
        <div className="beta-message">
          <div className="beta-icon">🔒</div>
          <p className="beta-title">عذراً، التطبيق حالياً في مرحلة تجريبية</p>
          <p className="beta-description">التسجيل متاح فقط لقائمة محدودة من العملاء. إذا كنت تعتقد أنه يجب أن يكون لديك صلاحية الوصول، يرجى التواصل مع مزود الخدمة الخاص بك.</p>
          <button type="button" className="back-btn" onClick={() => { setBetaRestricted(false); setStep(1); setError(''); }}>
            العودة
          </button>
        </div>
      </>
    );
  }

  return loginCard(
    <>
      <div className="login-header">
        {businessLogo}
        <h1>تسجيل الدخول</h1>
      </div>
      {renderStepDots()}
      {error && <div className="error-message">{error}</div>}
      {step === 1 && (
        <form onSubmit={handlePhoneSubmit} className="login-form">
          <div className="form-group">
            <label>رقم الهاتف</label>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="05xxxxxxxx" required dir="ltr" />
          </div>
          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? 'جاري الإرسال...' : 'إرسال كود التحقق'}
          </button>
        </form>
      )}
      {step === 2 && (
        <form onSubmit={handleVerifyCode} className="login-form">
          <div className="form-group">
            <label>كود التحقق (تم الإرسال على الرسائل النصية)</label>
            <input type="text" value={verificationCode} onChange={(e) => setVerificationCode(e.target.value)} placeholder="أدخل الكود" required dir="ltr" maxLength="6" />
          </div>
          <div className={`retry-counter ${remainingAttempts <= 1 ? 'retry-warning' : ''}`}>
            المحاولة <span>{4 - remainingAttempts}</span> من 3
          </div>
          <button type="submit" className="submit-btn" disabled={loading}>{loading ? 'جاري التحقق...' : 'تحقق'}</button>
          <button type="button" className="back-btn" onClick={() => { setStep(1); setVerificationCode(''); setError(''); setRemainingAttempts(3); }}>العودة</button>
        </form>
      )}
      {step === 3 && (
        <form onSubmit={handleCompleteRegistration} className="login-form">
          <div className="form-group">
            <label>الاسم الكامل</label>
            <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="أدخل اسمك الكامل" required />
          </div>
          <div className="form-group">
            <label>البلد</label>
            <input type="text" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="أدخل البلد" required />
          </div>
          <button type="submit" className="submit-btn" disabled={loading}>{loading ? 'جاري التسجيل...' : 'إكمال التسجيل'}</button>
          <button type="button" className="back-btn" onClick={() => setStep(2)}>العودة</button>
        </form>
      )}
    </>
  );
}

export default LoginPage;
